import type { ProcessCellForExportParams } from 'ag-grid-community'
import { hasProp, isObjectType, isPlainObject, isString } from 'remeda'
import { FORMATTER_TYPES } from '../constants'
import type { BadgeData } from './dom'

/**
 * Export Value Converters - Pure functions for converting complex cell data to export-friendly strings
 *
 * These converters extract displayable text from complex cell renderer data structures,
 * ensuring CSV/Excel exports show meaningful values instead of [object Object].
 */

// Type guards for complex cell data structures

// Extended badge data type that includes value property (from backend)
type BadgeCellData = BadgeData & {
  readonly value?: string
}

// Generic editable cell data (used by editors like number, date, etc.)
type EditableCellData = {
  readonly value: string | number
  readonly editable?: boolean
}

type MarkdownCellData = {
  readonly type: 'markdown'
  readonly value: string
  readonly html: string
}

type MixedCellData = {
  readonly formatter: string
  readonly value?: unknown
}

type CellDataWithValue = {
  readonly value: unknown
}

/** Type guard for editable cell data (number editors, date pickers, etc.) */
const isEditableCellData = (value: unknown): value is EditableCellData =>
  isObjectType(value) &&
  value !== null &&
  'value' in value &&
  'editable' in value &&
  (typeof value.value === 'string' || typeof value.value === 'number')

/** Type guard for badge data */
const isBadgeData = (value: unknown): value is BadgeCellData =>
  isObjectType(value) &&
  value !== null &&
  'label' in value && // Must have label for badge (not just value)
  !('editable' in value) // Not an editable cell

/** Type guard for stepper data */
const isStepperData = (
  value: unknown
): value is { label: string; value?: number } =>
  isObjectType(value) &&
  value !== null &&
  'label' in value &&
  typeof value.label === 'string'

/** Type guard for button data */
const isButtonData = (value: unknown): value is { label?: string } =>
  isObjectType(value) && value !== null && 'label' in value

/** Type guard for redirect data */
const isRedirectData = (value: unknown): value is { value?: string } =>
  isObjectType(value) && value !== null && 'value' in value

/** Type guard for markdown data */
const isMarkdownData = (value: unknown): value is MarkdownCellData =>
  isObjectType(value) &&
  value !== null &&
  'type' in value &&
  value.type === FORMATTER_TYPES.MARKDOWN &&
  'value' in value &&
  typeof value.value === 'string'

/** Type guard for progress data */
const isProgressData = (value: unknown): value is { value: string | number } =>
  isObjectType(value) &&
  value !== null &&
  'value' in value &&
  (typeof value.value === 'string' || typeof value.value === 'number')

/** Type guard for badges array data */
const isBadgesData = (
  value: unknown
): value is { badges: readonly BadgeCellData[] } =>
  isObjectType(value) &&
  value !== null &&
  'badges' in value &&
  Array.isArray(value.badges)

/** Type guard for image data */
const isImageData = (
  value: unknown
): value is { value: string; name: string } =>
  isObjectType(value) &&
  value !== null &&
  'value' in value &&
  'name' in value &&
  typeof value.name === 'string'

const isMixedCellData = (value: unknown): value is MixedCellData =>
  isPlainObject(value) &&
  hasProp(value, 'formatter') &&
  isString(value.formatter)

const hasValueProperty = (value: unknown): value is CellDataWithValue =>
  isPlainObject(value) && hasProp(value, 'value')

// Value extractors for each complex cell type

/** Extract displayable value from editable cell data (editors) */
export const extractEditableCellValue = (data: unknown): string => {
  if (!isEditableCellData(data)) {
    return String(data ?? '')
  }
  // Return the raw value from editable cells (number editors, date pickers, etc.)
  return String(data.value ?? '')
}

/** Extract displayable value from badge data */
export const extractBadgeValue = (data: unknown): string => {
  if (!isBadgeData(data)) {
    return String(data ?? '')
  }
  // Prefer label over value for display
  return data.label || data.value || ''
}

/** Extract displayable value from stepper data */
export const extractStepperValue = (data: unknown): string => {
  if (!isStepperData(data)) {
    return String(data ?? '')
  }
  // Use the label which shows current step name
  return data.label || ''
}

/** Extract displayable value from button data */
export const extractButtonValue = (data: unknown): string => {
  if (!isButtonData(data)) {
    return String(data ?? '')
  }
  // Return button label text
  return data.label || ''
}

/** Extract displayable value from icon data (icon name is already a string) */
export const extractIconValue = (data: unknown): string => {
  // Icon cells use string values directly
  return String(data ?? '')
}

/** Extract displayable value from redirect data */
export const extractRedirectValue = (data: unknown): string => {
  if (!isRedirectData(data)) {
    return String(data ?? '')
  }
  // Use the value which is the link text
  return data.value || ''
}

/** Extract original markdown text from rendered markdown data */
export const extractMarkdownValue = (data: unknown): string => {
  if (!isMarkdownData(data)) {
    return String(data ?? '')
  }

  return data.value
}

/** Extract displayable value from progress data */
export const extractProgressValue = (data: unknown): string => {
  if (!isProgressData(data)) {
    return String(data ?? '')
  }
  // Format as percentage
  return `${data.value}%`
}

/** Extract displayable value from badges array data */
export const extractBadgesValue = (data: unknown): string => {
  if (!isBadgesData(data)) {
    return String(data ?? '')
  }
  // Join all badge labels with comma
  return data.badges
    .map((badge) => badge.label || badge.value || '')
    .filter(Boolean)
    .join(', ')
}

/** Extract displayable value from image data */
export const extractImageValue = (data: unknown): string => {
  if (!isImageData(data)) {
    return String(data ?? '')
  }
  // Return filename for CSV/Excel export
  return data.name || ''
}

export const extractNestedValue = (data: unknown): string =>
  hasValueProperty(data) ? String(data.value ?? '') : String(data ?? '')

/**
 * Main export value converter - dispatches to appropriate extractor based on formatter type
 */
export const convertCellValueForExport = (
  value: unknown,
  formatter: string
): string => {
  // Handle null/undefined values
  if (value === null || value === undefined) {
    return ''
  }

  // Dispatch to appropriate converter based on formatter type
  switch (formatter) {
    case FORMATTER_TYPES.BADGE:
      return extractBadgeValue(value)
    case FORMATTER_TYPES.STEPPER:
      return extractStepperValue(value)
    case FORMATTER_TYPES.BUTTON:
      return extractButtonValue(value)
    case FORMATTER_TYPES.IMAGE:
      return extractImageValue(value)
    case FORMATTER_TYPES.ICON:
      return extractIconValue(value)
    case FORMATTER_TYPES.REDIRECT:
      return extractRedirectValue(value)
    case FORMATTER_TYPES.PROGRESS:
      return extractProgressValue(value)
    case FORMATTER_TYPES.BADGES:
      return extractBadgesValue(value)
    case FORMATTER_TYPES.MARKDOWN:
      return extractMarkdownValue(value)
    case FORMATTER_TYPES.NUMBER:
    case FORMATTER_TYPES.TEXT:
    case FORMATTER_TYPES.PLAINTEXT:
    case FORMATTER_TYPES.LONGTEXT:
      return extractNestedValue(value)
    default:
      // For simple formatters (text, number, etc.), return value as-is
      return extractNestedValue(value)
  }
}

/**
 * Create processCellCallback for AG Grid export params
 *
 * This callback is invoked for each cell during CSV/Excel export,
 * allowing us to convert complex objects to displayable strings.
 */
export const createProcessCellCallback = () => {
  // Define converter pipeline: [typeGuard, extractor] pairs
  // Order matters: editable cells first (most common), then specific formatters
  const converters: ReadonlyArray<
    readonly [(value: unknown) => boolean, (value: unknown) => string]
  > = [
    [isEditableCellData, extractEditableCellValue], // Most common for editor cells
    [isBadgeData, extractBadgeValue],
    [isStepperData, extractStepperValue],
    [isButtonData, extractButtonValue],
    [isMarkdownData, extractMarkdownValue],
    [isRedirectData, extractRedirectValue],
    [isProgressData, extractProgressValue],
    [isBadgesData, extractBadgesValue]
  ]

  return (params: ProcessCellForExportParams): string => {
    // Get value from params.value or extract from node data
    let value = params.value

    // For some export scenarios (e.g., range selection), params.value might be undefined
    // Extract from node data using the column's field property
    if (value === undefined && params.node && params.column) {
      const colDef = params.column.getColDef()
      const field = colDef.field

      // Try field first (most reliable), fallback to colId
      value = field
        ? params.node.data?.[field]
        : params.node.data?.[params.column.getColId()]
    }

    // Early return for simple values (not objects)
    if (!isObjectType(value) || value === null) {
      return String(value ?? '')
    }

    if (isMixedCellData(value)) {
      return convertCellValueForExport(value, value.formatter)
    }

    // Find first matching converter and apply extractor
    const converter = converters.find(([guard]) => guard(value))
    return converter ? converter[1](value) : String(value ?? '')
  }
}
